import { UserProfile, Tenant } from '../types';

export interface OrgInvitePayload {
  isInvite: boolean;
  orgTeamId: string;
  orgName: string;
  primaryRegionId: string;
  extendedRegions: string[];
  industry?: string;
  website?: string;
  address?: string;
  inviterUid?: string;
  inviterName?: string;
  invitedByUid?: string;
  invitedByName?: string;
}

/**
 * Normalizes an organization name for case-insensitive matching.
 */
export function normalizeOrgName(name?: string | null): string {
  return (name || '').toLowerCase().trim();
}

/**
 * Returns all organization users belonging to the same organization team as `targetUser`.
 */
export function getOrgTeamMembers(
  targetUser: UserProfile | null | undefined,
  allUsers: UserProfile[] = []
): UserProfile[] {
  if (!targetUser || targetUser.role !== 'organization') return [];

  const targetNormName = normalizeOrgName(targetUser.orgName);
  const targetTeamId = (targetUser.orgTeamId || '').trim();
  const targetUid = targetUser.uid;

  const orgUsers = (allUsers || []).filter(u => u.role === 'organization');

  const matched = orgUsers.filter(u => {
    if (u.uid === targetUid) return true;
    const uTeamId = (u.orgTeamId || '').trim();
    if (targetTeamId && (uTeamId === targetTeamId || u.uid === targetTeamId)) {
      return true;
    }
    if (uTeamId && uTeamId === targetUid) {
      return true;
    }
    const uNormName = normalizeOrgName(u.orgName);
    if (targetNormName && uNormName && targetNormName === uNormName) {
      return true;
    }
    return false;
  });

  // Ensure targetUser is in the list even if allUsers snapshot hasn't synced yet
  if (!matched.some(u => u.uid === targetUser.uid)) {
    matched.push(targetUser);
  }

  return matched.sort((a, b) => {
    const aLead = a.isOrgLead === true ? 1 : 0;
    const bLead = b.isOrgLead === true ? 1 : 0;
    if (aLead !== bLead) return bLead - aLead;
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * Determines whether the given organization user is the Lead of their organization.
 * - If `user.isOrgLead === true`, returns true.
 * - If `user.isOrgLead === false`, returns false (unless no one in the org is lead and they are the only member).
 * - For existing users where `isOrgLead` is undefined, if no other team member has `isOrgLead === true`,
 *   the first/existing user is recognized as the Organization Lead.
 */
export function isUserOrgLead(
  user: UserProfile | null | undefined,
  allUsers: UserProfile[] = []
): boolean {
  if (!user || user.role !== 'organization') return false;
  if (user.isOrgLead === true) return true;

  const teamMembers = getOrgTeamMembers(user, allUsers);
  const explicitLead = teamMembers.find(m => m.isOrgLead === true);
  if (explicitLead) {
    return explicitLead.uid === user.uid;
  }

  if (user.isOrgLead === false && teamMembers.length > 1) {
    return false;
  }

  // No explicit lead set yet among existing users for this org:
  // Pick the first existing user deterministically (or the user themselves if solo)
  const candidates = teamMembers.filter(m => m.isOrgLead !== false);
  if (candidates.length === 0) return teamMembers[0]?.uid === user.uid;

  const sorted = [...candidates].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (aTime !== bTime && aTime > 0 && bTime > 0) return aTime - bTime;
    return (a.uid || '').localeCompare(b.uid || '');
  });

  return sorted[0]?.uid === user.uid;
}

/**
 * Resolves the canonical `orgTeamId` for an organization user and their team.
 */
export function getCanonicalOrgTeamId(
  user: UserProfile | null | undefined,
  allUsers: UserProfile[] = []
): string {
  if (!user) return '';
  if (user.orgTeamId && user.orgTeamId.trim() !== '') {
    return user.orgTeamId.trim();
  }
  const teamMembers = getOrgTeamMembers(user, allUsers);
  const memberWithTeamId = teamMembers.find(m => m.orgTeamId && m.orgTeamId.trim() !== '');
  if (memberWithTeamId?.orgTeamId) {
    return memberWithTeamId.orgTeamId.trim();
  }
  const leadMember = teamMembers.find(m => m.isOrgLead === true) || teamMembers[0];
  return leadMember?.uid || user.uid;
}

/**
 * Checks if an Opportunity, Application, Placement, or Interview belongs to the active organization's shared team.
 */
export function doesRecordBelongToOrg(
  record: {
    organizationId?: string | null;
    organizationName?: string | null;
    orgName?: string | null;
    postedByUserId?: string | null;
    postedByUserEmail?: string | null;
    createdBy?: string | null;
  } | null | undefined,
  activeUser: UserProfile | null | undefined,
  allUsers: UserProfile[] = []
): boolean {
  if (!record || !activeUser || activeUser.role !== 'organization') return false;

  const teamMembers = getOrgTeamMembers(activeUser, allUsers);
  const teamUids = new Set<string>([
    activeUser.uid,
    ...(activeUser.orgTeamId ? [activeUser.orgTeamId] : []),
    ...teamMembers.map(m => m.uid),
    ...teamMembers.map(m => m.orgTeamId || '').filter(Boolean)
  ]);

  const teamEmails = new Set<string>(
    [activeUser.email, ...teamMembers.map(m => m.email)]
      .filter(Boolean)
      .map(e => e.toLowerCase().trim())
  );

  if (record.organizationId && teamUids.has(record.organizationId)) return true;
  if (record.postedByUserId && teamUids.has(record.postedByUserId)) return true;
  if (record.createdBy && (teamUids.has(record.createdBy) || teamEmails.has(record.createdBy.toLowerCase().trim()))) return true;
  if (record.postedByUserEmail && teamEmails.has(record.postedByUserEmail.toLowerCase().trim())) return true;

  const activeOrgName = normalizeOrgName(activeUser.orgName);
  const recordOrgName = normalizeOrgName(record.organizationName || record.orgName);
  if (activeOrgName && recordOrgName && activeOrgName === recordOrgName) {
    return true;
  }

  return false;
}

/**
 * Normalizes extendedRegions entries to lowercase tenant IDs where possible.
 */
export function normalizeExtendedRegionIds(
  extendedRegions: string[] | undefined,
  tenants: Tenant[] = [],
  primaryRegionId?: string
): string[] {
  if (!Array.isArray(extendedRegions)) return [];
  const pNorm = (primaryRegionId || '').toLowerCase().trim();
  const result = new Set<string>();

  extendedRegions.forEach(raw => {
    if (!raw) return;
    const norm = raw.toLowerCase().trim();
    if (!norm || norm === 'global' || norm === pNorm) return;
    const matchedTenant = (tenants || []).find(
      t => t.id.toLowerCase() === norm || t.name.toLowerCase() === norm
    );
    const resolvedId = matchedTenant ? matchedTenant.id.toLowerCase() : norm;
    if (resolvedId !== pNorm && resolvedId !== 'global') {
      result.add(resolvedId);
    }
  });

  return Array.from(result);
}

/**
 * Builds the prefilled invite link pointing to the main `/{primaryRegion}` page popout.
 */
export function buildOrgTeamInviteUrl(
  orgUser: UserProfile,
  allUsers: UserProfile[] = [],
  tenants: Tenant[] = [],
  fallbackRegionId?: string
): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const primaryRegionId = (
    (orgUser.tenantId && orgUser.tenantId !== 'global' && orgUser.tenantId !== 'apextech'
      ? orgUser.tenantId
      : fallbackRegionId) || 'spartanburg'
  ).toLowerCase().trim();

  const teamId = getCanonicalOrgTeamId(orgUser, allUsers);
  const orgName = orgUser.orgName || orgUser.name || 'Partner Organization';
  const extRegionIds = normalizeExtendedRegionIds(orgUser.extendedRegions, tenants, primaryRegionId);

  const params = new URLSearchParams();
  params.set('joinOrg', '1');
  params.set('region', primaryRegionId);
  params.set('orgTeamId', teamId);
  params.set('orgName', orgName);
  if (extRegionIds.length > 0) {
    params.set('extRegions', extRegionIds.join(','));
  }
  if (orgUser.industry) {
    params.set('industry', orgUser.industry);
  }
  if (orgUser.website) {
    params.set('website', orgUser.website);
  }
  if (orgUser.address) {
    params.set('address', orgUser.address);
  }
  if (orgUser.uid) {
    params.set('inviterUid', orgUser.uid);
  }
  if (orgUser.name) {
    params.set('inviterName', orgUser.name);
  }

  return `${origin}/${primaryRegionId}?${params.toString()}`;
}

/**
 * Parses the URL search query string for an organization team invite.
 */
export function parseOrgInviteFromSearch(searchString?: string): OrgInvitePayload | null {
  if (typeof window === 'undefined' && !searchString) return null;
  const rawSearch = searchString !== undefined ? searchString : window.location.search;
  const params = new URLSearchParams(rawSearch);
  const isJoinOrg = params.get('joinOrg') === '1' || params.get('joinOrg') === 'true';

  if (!isJoinOrg) return null;

  const pathSegments = typeof window !== 'undefined' ? window.location.pathname.split('/').filter(Boolean) : [];
  const pathRegion = pathSegments.length > 0 ? pathSegments[0].toLowerCase() : '';
  const primaryRegionId = (params.get('region') || params.get('primaryRegion') || pathRegion || 'spartanburg').toLowerCase().trim();
  const orgTeamId = (params.get('orgTeamId') || '').trim();
  const orgName = (params.get('orgName') || '').trim();
  const extRaw = params.get('extRegions') || '';
  const extendedRegions = extRaw
    .split(',')
    .map(r => r.trim().toLowerCase())
    .filter(r => r && r !== primaryRegionId && r !== 'global');

  if (!orgName && !orgTeamId) return null;

  const inviterUid = params.get('inviterUid') || params.get('invitedByUid') || undefined;
  const inviterName = params.get('inviterName') || params.get('invitedByName') || undefined;

  return {
    isInvite: true,
    orgTeamId,
    orgName,
    primaryRegionId,
    extendedRegions,
    industry: params.get('industry') || undefined,
    website: params.get('website') || undefined,
    address: params.get('address') || undefined,
    inviterUid,
    inviterName,
    invitedByUid: inviterUid,
    invitedByName: inviterName,
  };
}

export function parseOrgInviteFromUrl(): OrgInvitePayload | null {
  return parseOrgInviteFromSearch();
}

/**
 * Clears the organization invite query parameters from the browser URL without reloading.
 */
export function clearOrgInviteFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const keysToRemove = [
    'joinOrg',
    'region',
    'primaryRegion',
    'orgTeamId',
    'orgName',
    'extRegions',
    'industry',
    'website',
    'address',
    'inviterUid',
    'inviterName',
    'invitedByUid',
    'invitedByName',
  ];
  keysToRemove.forEach(k => url.searchParams.delete(k));
  const cleanSearch = url.searchParams.toString();
  const newPath = url.pathname + (cleanSearch ? `?${cleanSearch}` : '') + url.hash;
  window.history.replaceState({}, '', newPath);
}
